import AddIcon from '@mui/icons-material/Add';
import { Box, Grid, Stack } from '@mui/material';
import { DataGrid, GridColDef, GridSortModel, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IOurEntity,
  ourEntityList,
  saveColumnSettings,
  ColumnSettingsDTO,
  ColumnSettings,
  getColumnSettings,
} from 'wisestep-api';
import {
  WsTitleBanner,
  getDateUsingFormat,
  WsButton,
  WsIconEdit,
  WsIconMoreVer,
  WsTypography,
  WsTypoVariants,
  WsIconText,
  WsTypoColors,
} from 'wisestep-base';

import PopoverOnClick from '../../../features/clientListingPage/components/PopoverOnClick';
import { getCommonGridStyles } from '../../../features/clientListingPage/helper/ClientListingHelper';
import { OurEntityAction, useOurEntityAuthz } from '../../../hooks/useOurEntityAuthz';
import { CustomToolbar } from '../../invoicing/components/DataGridCustomToolBar';

const OurEntityList = () => {
  const [ourEntityListData, setOurEntityListData] = useState<IOurEntity[]>([]);
  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }]);
  const tableData: Record<string, unknown>[] = [];
  const [settings, setSettings] = useState<GridColumnVisibilityModel | null>(null);
  const isAuthorizedAddEntity = useOurEntityAuthz(OurEntityAction.ADD_ENTITY_DETAILS);
  const isAuthorizedEditOurEntity = useOurEntityAuthz(OurEntityAction.EDIT_ENTITY_DETAILS);

  const navigate = useNavigate();
  useEffect(() => {
    const getOurEntityList = async () => {
      const sortBy = !isEmpty(sortModel) ? sortModel[0].field : undefined;
      const sortOrder = !isEmpty(sortModel) ? sortModel[0].sort : undefined;
      const response = await ourEntityList(pageNo, pageSize, sortBy, sortOrder);
      setTotalRecord(response.data.totalRecord);
      setOurEntityListData(response.data.data);
    };
    getOurEntityList();
  }, [pageNo, pageSize, sortModel]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getColumnSettings(ColumnSettings.ORAGANISATION_ENTITY);
        setSettings(res.data.settings);
      } catch (error) {
        setSettings({
          Action: true,
          businessUnitId: true,
          corporateAddress: false,
          country: true,
          createdOn: true,
          legalName: false,
          name: true,
          registeredAddress: true,
          timezone: true,
        });
      }
    };
    if (settings === null) {
      fetchData();
    }
  }, [settings]);

  const onColumnChange = async (data: GridColumnVisibilityModel) => {
    const settingsPayload: ColumnSettingsDTO = {
      settings: data,
      tableKey: ColumnSettings.ORAGANISATION_ENTITY,
    };
    await saveColumnSettings(settingsPayload);
  };

  ourEntityListData?.map((item) => {
    tableData.push({
      id: item.id,
      name: item.Name,
      timezone: item.Timezone,
      registeredAddress: item.Registered_address,
      businessUnitId: item.Business_Name,
      country: item.Country,
      createdOn:
        item?.createdOn != undefined
          ? getDateUsingFormat(item.createdOn?.toString(), 'DD-MMM-YY')
          : '',
      legalName: item.Legal_Name,
      corporateAddress: item.Corporate_address,
    });
  });

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Organisation Name',
      flex: 1,
      renderCell: (params) => (
        <WsTypography
          variant={WsTypoVariants.SUB_TITLE}
          cursor="pointer"
          bold
          color={WsTypoColors.PRIMARY}
          onClick={() => navigate(`/organisationEntity/${params.id}/details`)}
        >
          {params.row['name']}
        </WsTypography>
      ),
    },
    {
      field: 'timezone',
      headerName: 'Organisation Time Zone',
      flex: 1,
    },
    {
      field: 'registeredAddress',
      headerName: 'Registered Address',
      flex: 1.5,
    },
    {
      field: 'legalName',
      headerName: 'Legal Name',
      flex: 1,
    },
    {
      field: 'corporateAddress',
      headerName: 'Corporate Address',
      flex: 1,
    },
    {
      field: 'country',
      headerName: 'Country',
      flex: 1,
    },
    {
      field: 'createdOn',
      headerName: 'Created On',
      flex: 1,
    },
    {
      field: 'Action',
      headerName: '',
      sortable: false,
      flex: 0.2,
      renderCell: (params) =>
        isAuthorizedEditOurEntity && (
          <PopoverOnClick element={<WsIconMoreVer />}>
            <Stack p={2} columnGap={1}>
              <WsIconText
                text="Edit"
                textCursor="pointer"
                onClick={() => navigate(`/organisationEntity/${params.id}/edit`)}
                icon={<WsIconEdit />}
              />
            </Stack>
          </PopoverOnClick>
        ),
    },
  ];

  return (
    <Box borderRadius={1.5}>
      <WsTitleBanner title="Organisation Entities" backButton={false} showBetaIcon={true} />
      {isAuthorizedAddEntity && (
        <Box>
          <Grid item xs={12} display="flex" justifyContent="flex-end" pt={2} pb={2}>
            <WsButton
              variant="contained"
              onClick={() => navigate('/organisationEntity/create')}
              startIcon={<AddIcon />}
            >
              Add Organisation Entity
            </WsButton>
          </Grid>
        </Box>
      )}
      {settings != null && tableData && tableData.length > 0 && (
        <Grid xs={12} sx={{ backgroundColor: 'white' }}>
          <DataGrid
            autoHeight={true}
            components={{ Toolbar: CustomToolbar }}
            columns={columns}
            rows={tableData}
            rowCount={totalRecord}
            onPageChange={(page) => setPageNo(page)}
            page={pageNo}
            pageSize={pageSize}
            rowsPerPageOptions={[25, 50, 75, 100]}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            sortingMode="server"
            paginationMode="server"
            onSortModelChange={(newSortModel) => {
              setPageNo(0);
              setSortModel(newSortModel);
            }}
            sortModel={sortModel}
            columnVisibilityModel={settings}
            onColumnVisibilityModelChange={(data) => {
              onColumnChange(data);
              setSettings(data);
            }}
            getRowHeight={() => 'auto'}
            sx={getCommonGridStyles()}
          />
        </Grid>
      )}
    </Box>
  );
};

export default OurEntityList;
